// Extension is explicit so `node --test` resolves it without a bundler.
import { GROWING_STATUS_ORDER, growingStatusLabel } from './growingStatuses.js';

/**
 * Turning a cluster into the numbers its overview shows.
 *
 * Kept apart from the component so the arithmetic — which statuses are
 * present, what each group totals, what the cluster is called — can be tested
 * without rendering anything. Order and labels come from `growingStatuses`,
 * the single registry every status-bearing surface reads.
 */

const num = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * A region's area in hectares.
 *
 * Reads `covered_ag_area_ha`: AMHPAC covered-ag STRUCTURE area (greenhouse,
 * macro-tunnel, shade-mesh), explicitly excluding open field. It is Mexico-only
 * and aggregated at (commodity, state), so it is present on roughly 0.5% of
 * regions — about a dozen greenhouse commodities, led by tomatoes, cucumbers
 * and bell peppers. Every consumer therefore treats a null as "no area" and
 * hides the figure rather than printing a dash.
 *
 * It is NOT total planted area. If a source for that ever lands, repoint this
 * accessor — the markup reads area from here and nowhere else.
 */
export const regionAreaHa = (region) =>
  num(region?.covered_ag_area_ha ?? region?.area_ha ?? region?.estimated_area_ha ?? null);

/**
 * The endpoint sends the literal string "N/A" for an empty varieties column,
 * so an emptiness check on its own is not enough.
 */
export const varietiesOf = (r) => {
  const v = String(r?.predominant_varieties ?? '').trim();
  return v && !/^n\s*\/?\s*a$/i.test(v) ? v : null;
};

/**
 * A location's producing windows. The endpoint has already formatted these as
 * ISO weeks ("Week 12 - Week 30"), so there is no week arithmetic to redo
 * here; a location with two seasons contributes both, de-duplicated.
 */
export const windowsOf = (r) => {
  const out = [];
  for (const season of Array.isArray(r?.seasons) ? r.seasons : []) {
    const w = String(season?.harvest_window ?? '').trim();
    if (w && !out.includes(w)) out.push(w);
  }
  return out;
};

/** A region's production in tonnes. */
export const regionProductionT = (region) =>
  num(region?.estimated_production_ton_num ?? region?.estimated_production_ton ?? null);

/** Sum that stays null when nothing contributed, so "no data" never reads as 0. */
const sumOrNull = (rows, pick) => {
  let total = 0;
  let any = false;
  for (const r of rows) {
    const v = pick(r);
    if (v != null) { total += v; any = true; }
  }
  return any ? total : null;
};

/**
 * The group's name: the region its members sit in — "Michoacán".
 *
 * Groups are now formed BY region, so every member shares one and this
 * returns it exactly. The tally is kept rather than reading the first row
 * because the function predates that grouping and still has to behave on any
 * mixed list a caller hands it; ties break on the first one seen, which is
 * stable for a given grouping.
 */
export function clusterRegionName(regions) {
  const counts = new Map();
  for (const r of regions || []) {
    const name = (r?.region || '').trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  let best = null;
  let bestN = 0;
  for (const [name, n] of counts) {
    if (n > bestN) { best = name; bestN = n; }
  }
  return best;
}

/**
 * @param {object} cluster  A `clusterRegions` entry.
 * @returns {{
 *   total: number,
 *   areaHa: number|null,
 *   productionT: number|null,
 *   regionName: string|null,
 *   groups: Array<{status: string, label: string, color: string,
 *                  regions: object[], count: number, areaHa: number|null}>
 * }}
 *   `groups` holds only the statuses with regions in this cluster, in display
 *   order, each already sorted by production so the three rows shown first are
 *   the ones worth seeing.
 */
/**
 * A region's production: its locations' figures, each distinct value counted
 * once.
 *
 * Two shapes live in `commodities_region_info` and this has to survive both.
 * Across 5,311 multi-location regions, 3,796 carry genuinely different
 * figures per location, 1,227 carry one REGION estimate stamped onto every
 * location (British Columbia: ten locations, all 4,175), and 288 are a mix.
 *
 * So a plain sum triples a stamped region, and taking one value throws away
 * four fifths of a real one — Estado de México's Valle de Bravo (142.2) and
 * Villa del Carbón (39.8) are both true, and reporting 39.8 for the region
 * loses the larger of them.
 *
 * Summing DISTINCT values is right for both: identical copies collapse to the
 * one estimate, different figures all count. The cost is two locations that
 * genuinely produce exactly the same tonnage being counted once — far rarer
 * than the stamping, and the safer way to be wrong.
 */
const regionProduction = (rows) => {
  const seen = new Set();
  let total = null;
  for (const r of rows) {
    const v = regionProductionT(r);
    if (v == null || seen.has(v)) continue;
    seen.add(v);
    total = (total ?? 0) + v;
  }
  return total;
};

export function summarizeCluster(cluster) {
  const regions = cluster?.regions || [];
  const groups = GROWING_STATUS_ORDER
    .map((status) => {
      const rows = regions.filter((r) => r.harvest_status === status);
      if (rows.length === 0) return null;
      const sorted = [...rows].sort(
        (a, b) => (regionProductionT(b) ?? -1) - (regionProductionT(a) ?? -1),
      );
      return {
        status,
        label: growingStatusLabel(status),
        regions: sorted,
        count: sorted.length,
        areaHa: sumOrNull(sorted, regionAreaHa),
      };
    })
    .filter(Boolean);

  return {
    total: regions.length,
    areaHa: sumOrNull(regions, regionAreaHa),
    productionT: regionProduction(regions),
    regionName: clusterRegionName(regions),
    groups,
  };
}

const compactFmt = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const fullFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** "48.2K" for the totals strip; an em dash when there is nothing to show. */
export const formatCompact = (v) => (v == null ? '—' : compactFmt.format(v));
/** "18,400" for group and row figures. */
export const formatFull = (v) => (v == null ? '—' : fullFmt.format(v));
